import { useEffect, useState } from 'react';
import { usePostHog } from '@posthog/react';
import type { PostHog } from 'posthog-js';

/**
 * PostHog feature flag keys used for experiments. Flag rollout percentages
 * (e.g. 50/50 splits) are configured in the PostHog dashboard.
 */
export const FeatureFlags = {
  appRedirectAfterSignup: 'app-redirect-after-signup',
  // After the preliminary offer calculator, offer the app instead of going
  // straight to KYC.
  preliminaryOfferAppRedirect: 'preliminary-offer-app-redirect',
} as const;

export type FeatureFlag = (typeof FeatureFlags)[keyof typeof FeatureFlags];

type FeatureFlagValue = string | boolean | undefined;

interface FeatureFlagValueState {
  isResolving: boolean;
  value: FeatureFlagValue;
}

export interface FeatureFlagState {
  // True until the value is final. Callers that branch navigation on a flag
  // must wait for this, otherwise the control path is taken first and the user
  // is yanked to the treatment path once the flags arrive.
  isResolving: boolean;
  isEnabled: boolean;
}

interface UseFeatureFlagOptions {
  // When false the flag is never read: no value is resolved and no
  // `$feature_flag_called` event is sent, so users excluded from an experiment
  // by other means do not get enrolled and dilute its results.
  enabled?: boolean;
}

// Flags normally arrive with the first page load. This only bounds the wait
// when PostHog never answers at all (blocked by an ad blocker, offline).
const RESOLVE_TIMEOUT_MS = 2500;

const RESOLVING: FeatureFlagValueState = { isResolving: true, value: undefined };

const RESOLVED_UNSET: FeatureFlagValueState = {
  isResolving: false,
  value: undefined,
};

const isPostHogConfigured = !!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;

const readFlag = (
  posthog: PostHog,
  flag: FeatureFlag
): FeatureFlagValueState => ({
  isResolving: false,
  value: posthog.getFeatureFlag(flag),
});

function useFeatureFlagValue(
  flag: FeatureFlag,
  options?: UseFeatureFlagOptions
): FeatureFlagValueState {
  const posthog = usePostHog();
  const shouldEvaluate = (options?.enabled ?? true) && isPostHogConfigured;
  // null until settled for the current evaluation, so a caller that flips
  // `enabled` on sees "resolving" instead of a stale unset value.
  const [state, setState] = useState<FeatureFlagValueState | null>(null);

  useEffect(() => {
    if (!shouldEvaluate) {
      return;
    }

    if (posthog.featureFlags?.hasLoadedFlags) {
      setState(readFlag(posthog, flag));
      return;
    }

    // Settle on the first answer only: PostHog reloads flags on identify, and a
    // later value must not flip a decision the caller has already acted on.
    let hasSettled = false;
    const settle = (next: FeatureFlagValueState) => {
      if (hasSettled) {
        return;
      }

      hasSettled = true;
      setState(next);
    };

    const timeoutId = setTimeout(
      () => settle(RESOLVED_UNSET),
      RESOLVE_TIMEOUT_MS
    );
    const unsubscribe = posthog.onFeatureFlags(() =>
      settle(readFlag(posthog, flag))
    );

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [posthog, flag, shouldEvaluate]);

  if (!shouldEvaluate) {
    return RESOLVED_UNSET;
  }

  return state ?? RESOLVING;
}

/**
 * Returns whether a PostHog feature flag is enabled for the current user.
 * Falls back to disabled when PostHog is not configured or never answers, so
 * users always end up on the control experience rather than stuck waiting.
 */
export function useFeatureFlag(
  flag: FeatureFlag,
  options?: UseFeatureFlagOptions
): FeatureFlagState {
  const { isResolving, value } = useFeatureFlagValue(flag, options);
  return { isResolving, isEnabled: !!value };
}
